import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SummaryApi from '../common';

// Dynamic Mega Menu:
// - Displays Main Categories (Root categories in database tree).
// - Under each Main Category, renders its Subcategories.
// - Clicking a Main Category navigates to `/product-category?category=<MainCategory>`.
// - Clicking a Subcategory navigates to `/product-category?category=<SubCategory>`.

const MegaMenu = ({ id, open, onClose }) => {
  const ref = useRef(null);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(SummaryApi.publicCategoryTree.url, { credentials: 'include' })
      .then(r => r.json())
      .then(json => {
        if (json?.success && Array.isArray(json.data) && json.data.length > 0) {
          setCategoryTree(json.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (open) {
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
    }
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      id={id}
      ref={ref}
      // NB: bg-white/98 isn't a Tailwind opacity step, so the class was never
      // generated and the panel rendered fully transparent. Solid white.
      className='absolute left-0 right-0 top-full bg-white shadow-2xl border-b border-slate-200 z-50 animate-fadeIn transition-all'
    >
      <div className='container mx-auto px-6 py-6'>
        {loading && categoryTree.length === 0 ? (
          <div className='py-8 text-center text-sm text-slate-500 animate-pulse'>
            Loading category directory...
          </div>
        ) : categoryTree.length === 0 ? (
          <div className='py-8 text-center text-sm text-slate-500'>
            No main categories created yet. Add root categories in Admin Panel → Categories.
          </div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8'>
            {categoryTree.map((mainCat) => {
              const hasChildren = Array.isArray(mainCat.children) && mainCat.children.length > 0;
              return (
                <div key={mainCat._id || mainCat.name} className='space-y-3 group/dept'>
                  {/* Main Category Header */}
                  <Link
                    to={`/product-category?category=${encodeURIComponent(mainCat.name)}`}
                    onClick={onClose}
                    className='flex items-center gap-2.5 pb-2 border-b-2 border-transparent group-hover/dept:border-brand-600 transition-all'
                  >
                    {mainCat.iconUrl ? (
                      <img src={mainCat.iconUrl} alt={mainCat.name} className='w-7 h-7 object-contain rounded-md bg-slate-50 p-0.5 border border-slate-200' />
                    ) : (
                      <span className='w-7 h-7 rounded-md bg-brand-100 text-brand-700 font-bold text-xs flex items-center justify-center border border-brand-200'>
                        {mainCat.name.substring(0, 2).toUpperCase()}
                      </span>
                    )}
                    <div>
                      <h3 className='font-bold text-base text-slate-900 group-hover/dept:text-brand-600 transition-colors'>
                        {mainCat.name}
                      </h3>
                      <p className='text-[11px] text-slate-500 font-normal'>
                        {hasChildren ? `${mainCat.children.length} subcategories` : 'Main Category'}
                      </p>
                    </div>
                  </Link>

                  {/* Subcategories List */}
                  <ul className='space-y-1 text-sm'>
                    <li>
                      <Link
                        to={`/product-category?category=${encodeURIComponent(mainCat.name)}`}
                        onClick={onClose}
                        className='block py-1 px-2.5 text-xs font-semibold text-brand-600 hover:text-brand-800 hover:bg-brand-50/60 rounded-md transition-colors'
                      >
                        All in {mainCat.name} →
                      </Link>
                    </li>
                    {hasChildren && mainCat.children.map((subCat) => (
                      <li key={subCat._id || subCat.name}>
                        <Link
                          to={`/product-category?category=${encodeURIComponent(subCat.name)}`}
                          onClick={onClose}
                          className='block py-1 px-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 rounded-md transition-colors text-xs font-medium'
                        >
                          {subCat.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Banner Strip inside Mega Menu */}
        <div className='mt-6 pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500'>
          <div className='flex items-center gap-2'>
            <span className='font-semibold text-slate-800'>Shopping for specific items?</span>
            <span>Select any Main Category or Subcategory above to browse all products.</span>
          </div>
          <Link
            to='/search'
            onClick={onClose}
            className='font-bold text-brand-600 hover:text-brand-800 transition-colors underline'
          >
            Browse All Catalogue →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MegaMenu;
